import React, { FC, useCallback, useMemo } from 'react';
import { get, trim, trimStart } from 'lodash';
import { Field } from 'react-final-form';
import { useSelector } from 'react-redux';
import { Error } from '../../../../components/src/lib/Error';
import { isAgency, ReduxStore, setPayment, TravellerPaymentStore, useActions, isBroker } from '@trip-ux/redux-store';
import { CreditCardForm } from '@trip-ux/components';

import { GenericTextInput } from './components';
import { ccStyles, ccStylesMobile } from './credit-card.styles';
import { AUTH_HEADERS, INPUT_MASK, LETTERS_AND_WHITESPACE_REGEX } from '@trip-ux/constants';
import CvvIFrame from './CvvIframe';

export const CreditTokenForm: FC<{
  paxId: number;
  onError: (tokenError: string[], paxId) => void;
  onCvvError: (cvvError: string[], paxId) => void;
  onDSToken: (e: { token: string; type: string }, paxId) => void;
  onDSCvvToken: (e: { cvvToken: string }, paxId) => void;
  isMobile: boolean;
  travellerPayments: TravellerPaymentStore;
  labels: {
    credit_card?: {
      ccInputLabel: string;
      expiryInputLabel: string;
      cvdInputLabel: string;
      disclaimer: string;
      iframeTitle: string;
      errors: Record<string, string>;
    };
    tokenNumber?: string;
    expiryDate?: string;
    expiryDateFormat?: string;
    cvv?: string;
    cardHolderName?: string;
  };
  onFieldCardErr?: (message: string) => void;
  onFieldExpiryErr?: (message: string) => void;
  onFieldCvvErr?: (message: string) => void;
  validateVar: boolean;
}> = ({
  paxId,
  onError,
  onCvvError,
  onDSToken,
  onDSCvvToken,
  travellerPayments,
  isMobile,
  labels,
  onFieldCardErr,
  onFieldCvvErr,
  onFieldExpiryErr,
  validateVar
}) => {
    const { config, userProfile } = useSelector((store: ReduxStore) => ({
      config: store.config,
      userProfile: store.userProfile
    }));
    const actions = useActions({ setPayment });

    const isInternal = window.location.hostname.includes('aks');

    const locale = get(config, 'locale');
    const isAdmin = get(config, 'isAdmin');

    const dsPaymentIframe =
      isAdmin || (isAgency(userProfile.organizationCategory) || isBroker(userProfile.organizationCategory))
        ? get(config, `options.settingsDSPath.${locale}`)
        : get(config, `options.settingsDSPath.${locale}-coverme`);
    const paymentIframe = get(config, 'options.paymentIframe');

    const onDSWrappedCvvToken = useCallback(
      ({ value, hasError }, id) => {
        if (hasError) {
          onCvvError([labels.credit_card.errors.mon_945], id);
        } else {
          onCvvError([], id);
          onDSCvvToken({ cvvToken: value }, id);
        }
      },
      [onDSCvvToken, labels, onCvvError]
    );

    const handleMockCvvChange = useCallback(e => {
      if (e.target.value.length >= 3) {
        onCvvError([], paxId);
        onDSCvvToken({ cvvToken: e.target.value }, paxId);
      }
    }, []);

    const validateCardHolderName = useCallback(
      value => {
        if (value && validateVar) {
          return onFieldCardErr(value.length > 0 ? '' : 'Invalid name');
        }
        return undefined;
      },
      [validateVar, onFieldCardErr]
    );

    const validateExpiryDate = useCallback(
      value => {
        if (value && validateVar) {
          return onFieldExpiryErr(value.length === FOUR ? '' : 'Invalid ExpiryDate');
        }
        return undefined;
      },
      [validateVar, onFieldExpiryErr]
    );

    const validateCVV = useCallback(
      value => {
        if (value && validateVar) {
          return onFieldCvvErr(value.length === THREE ? '' : 'Invalid cvv');
        }
        return undefined;
      },
      [validateVar, onFieldCvvErr]
    );

    const handleParse = useCallback(value => trimStart(value.replace(LETTERS_AND_WHITESPACE_REGEX, '')), []);

    const isLIAUser = useMemo(() => userProfile.userType === AUTH_HEADERS.LAN_ID, [userProfile]);
    const FOUR = 4;
    const THREE = 3;
    return (
      <>
        <div className="card-holder-container">
          <label htmlFor={`cardHolderName${paxId}`}>{labels.cardHolderName}</label>
          <Field
            name={`cardHolderName${paxId}`}
            component={GenericTextInput}
            className="card-holder-input"
            parse={handleParse}
            type="text"
            id={`cardHolderName${paxId}`}
            validate={validateCardHolderName}
            aria-labelledby={`cardHolderName${paxId}`}
            validateOnActive={false}
            required={true}
            inputOnBlur={e => {
              actions.setPayment({ ...travellerPayments[paxId], cardHolderName: e.target.value });
            }}
          />
        </div>
        {isAdmin && isLIAUser && (
          <div className="payment-container token-number">
            <label htmlFor={`tokenNumber${paxId}`}>{labels.tokenNumber}</label>
            <Field
              name={`tokenNumber${paxId}`}
              component={GenericTextInput}
              type="text"
              id={`tokenNumber${paxId}`}
              aria-labelledby={`tokenNumber${paxId}`}
              parse={value => trim(value)}
              validateOnActive={false}
              required={true}
              mask={INPUT_MASK.PAYMENT_TOKEN}
            />
          </div>
        )}
        {!isLIAUser && (
          <>
            <CreditCardForm
              {...(isMobile ? ccStylesMobile : ccStyles)}
              labels={labels.credit_card}
              id={`cc-${paxId}`}
              path={dsPaymentIframe}
              onError={onError}
              onDSToken={onDSToken}
              travellerPayments={travellerPayments}
              uri={paymentIframe}
              variant="COVER_ME"
            />
            <div className="expiry-date-container">
              <div className="expiry-date-row">
                <div className="expiryDate">
                  <label htmlFor={`expiryDate${paxId}`} className="text-nowrap">
                    {labels.expiryDate}
                    <span className="font-weight-light">{labels.expiryDateFormat}</span>
                  </label>
                  <Field
                    name={`expiryDate${paxId}`}
                    component={GenericTextInput}
                    className="expiryDate-input"
                    type="text"
                    id={`expiryDate${paxId}`}
                    aria-labelledby={`expiryDate${paxId}`}
                    validateOnActive={false}
                    maxLength={4}
                    validate={validateExpiryDate}
                    required={true}
                    inputOnBlur={e => {
                      actions.setPayment({ ...travellerPayments[paxId], expiryDate: e.target.value });
                    }}
                  />
                </div>
                {isInternal ? (
                  <div className="cvv">
                    <label htmlFor={`cvv${paxId}`}>{labels.cvv}</label>
                    <Field
                      name={`cvv${paxId}`}
                      component={GenericTextInput}
                      className="cvv-input"
                      type="text"
                      id={`cvv${paxId}`}
                      aria-labelledby={`cvv${paxId}`}
                      validateOnActive={false}
                      maxLength={3}
                      inputOnChange={handleMockCvvChange}
                      validate={validateCVV}
                      required={true}
                    />
                  </div>
                ) : (
                  <div className="cvv">
                    <label htmlFor={`cvv${paxId}`}>{labels.cvv}</label>
                    <CvvIFrame
                      path={dsPaymentIframe}
                      id={paxId}
                      onMessage={onDSWrappedCvvToken}
                      iframeUrl={paymentIframe}
                    />
                    {travellerPayments && travellerPayments[paxId] && (
                      <Error errors={travellerPayments[paxId]?.cvvTokenErr} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </>
    );
  };

CreditTokenForm.displayName = 'PaymentForm';
export default CreditTokenForm;
